import React from "react";
import { Card } from "@/components/ui/card";
import { SeverityBadge } from "@/components/StatusBadge";
import { fmtDate, fmtPLN } from "@/lib/api";

export function AlertItem({ alert, onAck, onClose, currentUserRole, currentUserId }) {
  const isCritical = alert.level === "critical";
  const isOwnRelated = alert.related_user_id && alert.related_user_id === currentUserId;
  const canAck = !isOwnRelated;
  const canClose = (currentUserRole === "owner" || !isCritical) && !isOwnRelated;

  const sideBar =
    alert.level === "critical"
      ? "bg-[var(--tt-critical-fg)]"
      : alert.level === "urgent"
        ? "bg-[var(--tt-urgent-fg)]"
        : alert.level === "warning"
          ? "bg-[var(--tt-warn-fg)]"
          : "bg-[var(--tt-info-fg)]";

  return (
    <Card className="tt-card overflow-hidden" data-testid="alert-row">
      <div className="flex gap-3 p-3 md:p-4">
        <div className={`w-1 self-stretch rounded-full ${sideBar} ${isCritical ? "tt-critical-pulse" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge level={alert.level} />
            {alert.department ? (
              <span className="text-xs font-semibold text-[var(--tt-muted)] uppercase tracking-wide">{alert.department}</span>
            ) : null}
            <span className="text-xs text-[var(--tt-muted)] tt-mono">{fmtDate(alert.created_at)}</span>
          </div>
          <div className="mt-2 font-semibold text-[var(--tt-text)]">{alert.title}</div>
          <div className="mt-1 text-sm text-[var(--tt-muted)] leading-snug">{alert.description}</div>
          {alert.amount ? (
            <div className="mt-2 text-sm tabular-nums tt-mono">Kwota: {fmtPLN(alert.amount)}</div>
          ) : null}
          {alert.suggested_action ? (
            <div className="mt-2 text-xs text-[var(--tt-muted)] italic">Sugerowane: {alert.suggested_action}</div>
          ) : null}
          {alert.status === "closed" && (
            <div className="mt-3 p-2 rounded-md bg-[var(--tt-surface-2)] border border-[var(--tt-border)]" data-testid="alert-closed-info">
              <div className="text-[11px] uppercase tracking-wide text-[var(--tt-muted)] font-semibold">Zamknięte {alert.closed_at ? `• ${fmtDate(alert.closed_at)}` : ""}</div>
              {alert.close_reason ? (
                <div className="mt-1 text-xs text-[var(--tt-text)] whitespace-pre-wrap break-words">
                  Powód: {alert.close_reason}
                </div>
              ) : (
                <div className="mt-1 text-xs italic text-[var(--tt-muted)]">Brak podanego powodu.</div>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {alert.status === "open" && canAck && (
              <button
                onClick={() => onAck?.(alert.id)}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-[var(--tt-primary)] text-[var(--tt-primary-fg)] hover:bg-[var(--tt-primary-hover)] hover:text-[var(--tt-primary-fg)] transition-colors tt-focusable"
                data-testid="alert-acknowledge-button"
              >
                Potwierdź
              </button>
            )}
            {alert.status !== "closed" && canClose && (
              <button
                onClick={() => onClose?.(alert)}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-[var(--tt-border)] bg-[var(--tt-surface)] text-[var(--tt-text)] hover:bg-[var(--tt-surface-2)] transition-colors"
                data-testid="alert-close-button"
              >
                Zamknij
              </button>
            )}
            {isOwnRelated && (
              <span className="text-xs italic text-[var(--tt-muted)]">Dotyczy Twoich działań — nie możesz tego zamknąć.</span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
